import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject, BehaviorSubject, forkJoin } from 'rxjs';

import { Coordinate } from 'ol/coordinate';
import * as olProj from 'ol/proj';

import { SegmentUtilities } from '../shared/utils/segment-utilities';
import { SegmentPoint } from '../models/segment-point';
import { ElevationPoiType } from '../models/elevation-poi';
import { map, takeUntil } from 'rxjs/operators';
import { TT_LIB_ENV_CONFIG } from '@tt-lib/tt-lib-env.config.token';
import { AppEnvironmentConfig } from '@tt-lib/interfaces/app-environment-config';

@Injectable({
  providedIn: 'root'
})
export class ElevationService {

  private elevationServiceUrl: string;
  private computedPoints: SegmentPoint[];
  private pois: ElevationPoiType[];

  public standardDistance: number = 25;//20;
  private pointsPerChunk: number = 250;
  private elevationDeltaTreshold: number = 3;

  private pointChunks: SegmentPoint[][];

  private comingPointsSubject: Subject<SegmentPoint[]>;
  readonly $comingPoints: Observable<SegmentPoint[]>;

  private isComputingPointsSubject: Subject<boolean>;
  readonly $isComputingPoints: Observable<boolean>;

  private isBuildingProfileSubject: Subject<boolean>;
  readonly $isBuildingProfile: Observable<boolean>;

  private profileSummarySubject: Subject<any>;
  readonly $profileSummary: Observable<any>;

  private poisSubject: Subject<any>;
  readonly $pois: Observable<any>;

  private fetchElevationsTimestamp: number = 0;
  private cancelFetchSubject: Subject<void>;
  readonly $cancelFetch: Observable<any>;

  private _fetchError: WritableSignal<boolean> = signal(false);
  public readonly fetchError = this._fetchError.asReadonly();

  private appEnvironmentConfig: AppEnvironmentConfig = inject(TT_LIB_ENV_CONFIG);

  constructor(
    private http: HttpClient
  ) {
    this.elevationServiceUrl = this.appEnvironmentConfig.ELEVATION_BASEURL + this.appEnvironmentConfig.URL_SUFFIX + 'fetch';

    this.comingPointsSubject = new Subject<SegmentPoint[]>();
    this.$comingPoints = this.comingPointsSubject.asObservable();

    this.isComputingPointsSubject = new BehaviorSubject(true);
    this.$isComputingPoints = this.isComputingPointsSubject.asObservable();

    this.isBuildingProfileSubject = new BehaviorSubject(false);
    this.$isBuildingProfile = this.isBuildingProfileSubject.asObservable();

    this.profileSummarySubject = new Subject<any>();
    this.$profileSummary = this.profileSummarySubject.asObservable();

    this.poisSubject = new Subject<any>();
    this.$pois = this.poisSubject.asObservable();

    this.cancelFetchSubject = new Subject<void>();
    this.$cancelFetch = this.cancelFetchSubject.asObservable();
  }

  computePath(coordinates: Coordinate[], projection: olProj.Projection) {
    this.isComputingPointsSubject.next(true);
    const util: SegmentUtilities = new SegmentUtilities(coordinates, projection);
    try {
      this.computedPoints = util.calculatePoints(this.standardDistance);
      this.pointChunks = this.splitArrayIntoChunk();
    } catch (e) {
      console.log("error on computing points");
    } finally {
      this.isComputingPointsSubject.next(false);
    }
  }

  public fetchElevations() {

    this._fetchError.set(false);

    const localFetchElevationsTimestamp = new Date().getTime();
    this.cancelOrRestartFetchElevationsProcess(localFetchElevationsTimestamp);

    this.isBuildingProfileSubject.next(true);
    this.setMinimalProfile();

    let $responses = [];
    this.pointChunks.forEach(chunk => {
      $responses.push(this.sendElevationRequest(chunk /*,localFetchElevationsTimestamp*/).pipe(map((response) => this.handleElevationResponse(response))));
    });

    forkJoin($responses).subscribe(() => {
      if (localFetchElevationsTimestamp === this.fetchElevationsTimestamp) {
        this.fetchElevationsTimestamp = 0;
        let elapsed = Date.now() - localFetchElevationsTimestamp;
        console.log("all data received from " + $responses.length + " requests in " + elapsed + " miliseconds.");
        this.fillSlopesAndGradients();
        this.comingPointsSubject.next(this.computedPoints);
        if (this.pois) this.poisSubject.next(this.locatePois());
        this.profileSummarySubject.next(this.getProfileSummary());
        this.isBuildingProfileSubject.next(false);
      } else if (this.fetchElevationsTimestamp === 0) {
        //no data & no other process has started
        this.profileSummarySubject.next({
          "error": "missing data"
        });
        this.isBuildingProfileSubject.next(false);

        //probably already true
        this._fetchError.set(true);

        console.log('elevation fetch interrupted');
      }
    },
      (error) => {
        if (localFetchElevationsTimestamp === this.fetchElevationsTimestamp || this.fetchElevationsTimestamp === 0) {
          //no data & no other process has started
          this.fetchElevationsTimestamp = 0;
          this.profileSummarySubject.next({
            "error": "missing data"
          });
          this.isBuildingProfileSubject.next(false);

          //normally already true, set by handleElevationResponse
          this._fetchError.set(true);

          console.log('elevation fetch interrupted', error);
        }
      });
  }

  // deliver heightless points to the chart component to adjust x-scale before receive real data
  private setMinimalProfile() {
    for (const cpt of this.computedPoints) {
      cpt.setEstimatedElevation(true);
    }
    this.comingPointsSubject.next(this.computedPoints);
  }

  public resetData() {
    this.cancelOrRestartFetchElevationsProcess();

    this._fetchError.set(false);

    this.comingPointsSubject.next(null);
    this.profileSummarySubject.next(null);

    //this call sometimes creates a problem for the poi's in the graph when switching between routes
    //but without this call the old poi's still briefly appear when the new graph appears
    this.pois = [];
    this.poisSubject.next(null);

    this.isBuildingProfileSubject.next(false);
  }

  /**
   * this method only cancels ongoing requests and it only changes property this.fetchElevationsTimestamp 
   * 
   * @param restartFetchElevationsTimestamp optional, only necessary for a cancellation and immediate restart
   */
  private cancelOrRestartFetchElevationsProcess(restartFetchElevationsTimestamp?: number) {
    const oldFetchElevationsTimestamp = this.fetchElevationsTimestamp;
    this.fetchElevationsTimestamp = typeof restartFetchElevationsTimestamp === 'number' && restartFetchElevationsTimestamp > 0 ? restartFetchElevationsTimestamp : 0;
    if (oldFetchElevationsTimestamp > 0) {
      this.cancelFetchSubject.next();
    }
  }

  // for pdf print we want to be able to update the look& feel of the graph and turn back to the normal look afterwards
  public refreshGraph() {
    this.comingPointsSubject.next(this.computedPoints);
    this.profileSummarySubject.next(this.getProfileSummary());
  }

  private splitArrayIntoChunk() {
    // make deep copy of computedPoints
    let points: SegmentPoint[] = JSON.parse(JSON.stringify(this.computedPoints));

    // first chunk is with first and last point
    const firstChunk = [points.shift(), points.pop()];

    var chunks = [], i = 0, n = points.length;
    const chunksNumber: number = 1 + Math.ceil(n / this.pointsPerChunk)

    for (let j = 0; j < chunksNumber; j++) {
      chunks[j] = [];
    }

    // set firstChunk
    chunks[0] = firstChunk;

    // set next chunks
    while (i < n) {
      for (let j = 1; j < chunksNumber; j++) {
        if (i === n) {
          break;
        }
        chunks[j].push(points[i]);
        i++;
      }
    }

    return chunks;
  }

  private sendElevationRequest(points: SegmentPoint[] /*, localFetchElevationsTimestamp: number*/): Observable<any> {
    // console.log("sent : " + JSON.stringify(points));
    const headers = new HttpHeaders()
      .append(
        'Content-Type',
        'application/json'
      );
    return this.http
      .post<any>(this.elevationServiceUrl, points, {
        headers: headers,
      }).pipe(
        takeUntil(this.cancelFetchSubject)
        /*,takeWhile(() => localFetchElevationsTimestamp === this.fetchElevationsTimestamp)*/
      );
  }

  private handleElevationResponse(res: any) {
    try {
      let points: SegmentPoint[] = new Array<SegmentPoint>();
      res.forEach((pt: any) => {
        const sp = new SegmentPoint(parseInt(pt.id), parseFloat(pt.x), parseFloat(pt.y), parseFloat(pt.z), parseFloat(pt.distanceFromStart));
        points.push(sp);
      });
      this.fillElevations(points);
      this.comingPointsSubject.next(this.computedPoints);
    } catch (error) {

      //the profile will be hidden directly, showing a reload button
      this._fetchError.set(true);
      //we don't want the remaining responses anymore, we let the user click the reload button
      this.cancelOrRestartFetchElevationsProcess();

      console.error(error);
      throw Error("An error occured in method handleElevationResponse, we cancelled any remaining requests.");
    }
  }

  private fillElevations(receivedPoints: SegmentPoint[]) {
    for (const rpt of receivedPoints) {
      const pointToUpdate = this.computedPoints.find(el => rpt.getId() === el.getId());
      // add check on pointToUpdate (after resetData, the first time this point is not found, undefined, afterwards this is always ok)
      if (pointToUpdate) {
        pointToUpdate.setElevation(rpt.getElevation());
        pointToUpdate.setEstimatedElevation(false);
      }
    }
    this.interpolateElevation();
  }

  private interpolateElevation() {
    let previousKnownElevation = 0;
    let pointsToEstimate = [];
    for (const cpt of this.computedPoints) {
      if (cpt.isEstimatedElevation()) {
        pointsToEstimate.push(cpt);
      } else {
        const deltaElevation = (cpt.getElevation() - previousKnownElevation) / (pointsToEstimate.length + 1);
        for (let i = 0; i < pointsToEstimate.length; i++) {
          pointsToEstimate[i].setElevation(previousKnownElevation + (i + 1) * deltaElevation);
        }
        // reset
        previousKnownElevation = cpt.getElevation();
        pointsToEstimate = [];
      }
    }
  }

  private fillSlopesAndGradients() {
    let positiveGradient = 0
    let negativeGradient = 0;
    let previousPoint = this.computedPoints[0];
    let lastSignificantElevation = previousPoint.getElevation();

    this.computedPoints.forEach((point: SegmentPoint) => {
      const deltaDistance = point.getDistanceFromStart() - previousPoint.getDistanceFromStart();
      let deltaElevation: number;

      // smooth elevation
      const elv = point.getElevation();
      if (Math.abs(elv - lastSignificantElevation) > this.elevationDeltaTreshold) {
        deltaElevation = elv - lastSignificantElevation;
        lastSignificantElevation = elv;
      } else {
        deltaElevation = 0;
      }

      // slope: cannot be calculated like this if smoothing
      // const slope = (deltaElevation == 0) ? 0 : Math.round(100 * deltaElevation / deltaDistance);
      // point.setSlope(slope);

      // gradient
      if (deltaElevation > 0) {
        positiveGradient += deltaElevation;
      } else {
        negativeGradient += deltaElevation;
      }
      point.setGradient([positiveGradient, negativeGradient]);
      //console.log(point.getId() + " " + point.getDistanceFromStart() + " " + point.getElevation() + " " + point.getGradient()[0]);
      previousPoint = point;
    });
  }

  private getProfileSummary() {
    const origin = this.computedPoints[0];
    const destination = this.computedPoints[this.computedPoints.length - 1];
    let summary = {
      "origin": {
        "x": origin.getX(),
        "y": origin.getY(),
        "elevation": origin.getElevation()
      },
      "destination": {
        "x": destination.getX(),
        "y": destination.getY(),
        "elevation": destination.getElevation()
      },
      "path": {
        "distance": destination.getDistanceFromStart(),
        "positive gradient": destination.getGradient()[0],
        "negative gradient": destination.getGradient()[1],
        "total gradient": destination.getGradient()[0] + destination.getGradient()[1]
      }
    }
    return summary;
  }

  // methods for points of interest 
  public setPois(pois: ElevationPoiType[]) {
    this.pois = pois;
  }

  private locatePois() {
    const maxElevation = this.getMaxElevation();
    let poisData: any[] = [];
    this.pois.forEach((poi: ElevationPoiType, index) => {
      const closestPoint = this.getClosestSegmentPoint([poi.x, poi.y], this.computedPoints);
      const id = index + 1;
      poisData.push([closestPoint.getId(), maxElevation, id, poi.label, poi.x, poi.y]);
    });
    //add maxElevation for start and end marker
    const pois = {
      maxElevation: maxElevation,
      poisData: poisData
    }
    return pois;
  }

  private getClosestSegmentPoint(point: Coordinate, points: SegmentPoint[]) {
    let closestPoint: SegmentPoint;
    let squareDistance: number = Number.MAX_SAFE_INTEGER;
    points.forEach((pt: SegmentPoint) => {
      const sqDist = Math.pow((pt.getX() - point[0]), 2) + Math.pow((pt.getY() - point[1]), 2);
      if (sqDist < squareDistance) {
        squareDistance = sqDist;
        closestPoint = pt;
      }
    });
    return closestPoint;
  }

  private getMaxElevation() {
    let maxEl = 0;
    this.computedPoints.forEach((pt: SegmentPoint) => {
      const el = pt.getElevation();
      if (el > maxEl) {
        maxEl = el;
      }
    });
    return maxEl;
  }

  getElevationForSingleCoordinate(coordinate: Coordinate) {
    this.cancelOrRestartFetchElevationsProcess(Date.now());
    return this.sendElevationRequest([new SegmentPoint(0, coordinate[0], coordinate[1])] /*,this.fetchElevationsTimestamp*/);
  }

}
